import { useEffect } from "react";
import { setLocale } from "yup";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";

/**
 * Hook that configures Yup with localized error messages from Contentful.
 * This eliminates the need to pass field-specific error messages to every schema.
 *
 * Fields must use .label() to set their display name, and Yup will automatically
 * generate field-specific error messages like "Email is required" instead of
 * generic "This field is required".
 *
 * The hook runs once on mount to set up the localized messages.
 *
 * @example
 * // In your component:
 * useApplyLocalizedErrorMessages();
 *
 * // In schema:
 * email: string()
 *   .label("Email")
 *   .required() // Will automatically show "Email is required"
 */
export const useApplyLocalizedErrorMessages = () => {
    const { get } = useMicrocopy();

    const applyMessages = () => {
        setLocale({
            mixed: {
                // Use a function that receives the field's label
                required: ({ label }) => {
                    if (label) {
                        // Generate field-specific message using the dynamic template
                        return get(CommonCMS.shared, CommonCMS.error.requiredFieldDynamic, {
                            replacements: { fieldName: label },
                        });
                    }
                    // Fallback to generic message if no label is set
                    return get(CommonCMS.shared, CommonCMS.error.requiredFieldError);
                },
            },
        });
    };

    useEffect(() => {
        applyMessages();
    }, []);
};
